import axios from 'axios';
import { API_BASE_URL } from '../components/config/api';
import { addClientBuildHeader } from '../utils/clientBuild';

// Create axios instance for admin API
const adminApi = axios.create({
  baseURL: `${API_BASE_URL}/api/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('xentrix_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return addClientBuildHeader(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses and errors
adminApi.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to login if unauthorized
      localStorage.removeItem('xentrix_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const adminAPI = {
  // Dashboard
  getDashboard: () => adminApi.get('/dashboard'),

  // Daily money reconciliation (orders vs fiscal vs POS, per store, any past day)
  getReconciliation: (date: string) => adminApi.get('/reconciliation', { params: { date } }),

  // Stores
  getStores: () => adminApi.get('/stores'),
  getStore: (storeId: number) => adminApi.get(`/stores/${storeId}`),
  createStore: (data: any) => adminApi.post('/stores', data),
  updateStore: (storeId: number, data: any) => adminApi.put(`/stores/${storeId}`, data),
  deleteStore: (storeId: number) => adminApi.delete(`/stores/${storeId}`),

  // Employees
  getEmployees: () => adminApi.get('/employees'),
  getEmployee: (employeeId: number) => adminApi.get(`/employees/${employeeId}`),
  createEmployee: (data: any) => adminApi.post('/employees', data),
  updateEmployee: (employeeId: number, data: any) => adminApi.put(`/employees/${employeeId}`, data),
  deleteEmployee: (employeeId: number) => adminApi.delete(`/employees/${employeeId}`),

  // Roles
  getRoles: () => adminApi.get('/roles'),

  // Audit Logs
  getAuditLogs: (params?: { store_id?: number; employee_id?: number; limit?: number }) => 
    adminApi.get('/audit-logs', { params }),

  // Master access (πασπαρτού password/PIN) — status only; hashes never leave the server
  getMasterAccess: () => adminApi.get('/master-access'),
  setMasterAccess: (password?: string, pin?: string) => adminApi.put('/master-access', { password, pin }),

  // Billing / mini-CRM
  getBillingOverview: () => adminApi.get('/billing/overview'),
  getStoreBilling: (storeId: number) => adminApi.get(`/billing/store/${storeId}`),
  saveStoreBilling: (storeId: number, data: any) => adminApi.put(`/billing/store/${storeId}`, data),
  // «Μαζική συμπλήρωση» (27/10/2026): μόνο κύκλος + τιμή, όλα μαζί ή τίποτα.
  saveBillingBulk: (stores: { store_id: number; billing_cycle: string; agreed_price: string | number | null }[]) =>
    adminApi.put('/billing/bulk', { stores }),
  saveStoreBillingFeatures: (storeId: number, features: { feature_id: number; price_override?: number | null }[]) =>
    adminApi.put(`/billing/store/${storeId}/features`, { features }),
  addBillingPayment: (storeId: number, data: any) => adminApi.post(`/billing/store/${storeId}/payments`, data),
  deleteBillingPayment: (paymentId: number) => adminApi.delete(`/billing/payments/${paymentId}`),
  addStoreHardware: (storeId: number, data: any) => adminApi.post(`/billing/store/${storeId}/hardware`, data),
  updateStoreHardware: (hardwareId: number, data: any) => adminApi.put(`/billing/hardware/${hardwareId}`, data),
  deleteStoreHardware: (hardwareId: number) => adminApi.delete(`/billing/hardware/${hardwareId}`),
  getBillingFeatures: () => adminApi.get('/billing/features'),
  createBillingFeature: (data: any) => adminApi.post('/billing/features', data),
  updateBillingFeature: (featureId: number, data: any) => adminApi.put(`/billing/features/${featureId}`, data),

  // Oxygen Credentials
  saveOxygenCredentials: (storeId: number, apiToken: string) =>
    adminApi.post(`/stores/${storeId}/oxygen-credentials`, { api_token: apiToken }),
  getOxygenCredentials: (storeId: number) => 
    adminApi.get(`/stores/${storeId}/oxygen-credentials`),

  // Fiscal Documents (cross-store)
  getFiscalDocuments: (params?: { store_id?: number; status?: string; limit?: number; offset?: number; from?: string; to?: string }) =>
    adminApi.get('/fiscal-documents', { params }),
  getFiscalSummary: () => adminApi.get('/fiscal-summary'),
  retryFiscalDocument: (documentId: number) => adminApi.post(`/fiscal-documents/${documentId}/retry`),
  cancelFiscalDocument: (documentId: number) => adminApi.post(`/fiscal-documents/${documentId}/cancel`),

  // Platform Credentials (cross-store)
  getPlatformCredentials: () => adminApi.get('/platform-credentials'),
  updatePlatformCredentials: (storeId: number, platformName: string, data: any) =>
    adminApi.put(`/platform-credentials/${storeId}/${platformName}`, data),
  deletePlatformCredentials: (storeId: number, platformName: string) =>
    adminApi.delete(`/platform-credentials/${storeId}/${platformName}`),

  // Password & PIN Reset
  resetStorePassword: (storeId: number, newPassword: string) =>
    adminApi.post(`/stores/${storeId}/reset-password`, { new_password: newPassword }),
  resetEmployeePin: (employeeId: number, newPin: string) =>
    adminApi.post(`/employees/${employeeId}/reset-pin`, { new_pin: newPin }),

  // Catalog / Products (read-only)
  getStoreCategories: (storeId: number) => adminApi.get(`/stores/${storeId}/categories`),
  getStoreProducts: (storeId: number, params?: { category_id?: number; search?: string; available?: string }) =>
    adminApi.get(`/stores/${storeId}/products`, { params }),
};
